import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ClinicalDataLanguageNotice } from '@/components/ClinicalDataLanguageNotice';
import ExplainButton from './ExplainButton';
import { formatRecordDate, isThisAdmission, slugify } from './records-utils';

interface Condition {
  id?: string;
  name: string;
  clinicalStatus?: string;
  onsetDate?: string;
}

interface ConditionsSectionProps {
  conditions: Condition[];
  admissionDate?: string;
  /** Plain-language names and meanings from the startup payload, matched to the record by name. */
  items?: Array<{ name: string; plainName: string; meaning?: string }>;
  patientFirstName: string;
  onAskQuestion: (question: string) => void;
  variant: 'main' | 'rail';
}

type GroupId = 'thisAdmission' | 'ongoingFromBefore';

export default function ConditionsSection({ conditions, admissionDate, items, patientFirstName, onAskQuestion, variant }: ConditionsSectionProps) {
  const { t, i18n } = useTranslation();
  const rowPadding = variant === 'rail' ? 'px-3' : 'px-4';
  const [openId, setOpenId] = useState<string | null>(null);

  if (!conditions || conditions.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-4">{t('records.noConditionsDataAvailable')}</p>;
  }

  const byName = new Map((items || []).map((item) => [item.name.toLowerCase(), item]));

  const groups: Array<{ id: GroupId; label: string; conditions: Condition[] }> = [
    { id: 'thisAdmission', label: t('records.groupThisAdmission'), conditions: [] },
    { id: 'ongoingFromBefore', label: t('records.groupOngoingFromBefore'), conditions: [] },
  ];
  conditions.forEach((condition) => {
    groups[isThisAdmission(condition, admissionDate) ? 0 : 1].conditions.push(condition);
  });

  const renderRow = (condition: Condition, index: number, groupId: GroupId) => {
    const rowId = condition.id || `${groupId}-${index}`;
    const item = byName.get(condition.name.toLowerCase());
    const plainName = item?.plainName;
    const meaning = (item?.meaning || '').trim();
    const showClinicalName = !!plainName && plainName.toLowerCase() !== condition.name.toLowerCase();
    const firstNoted = formatRecordDate(condition.onsetDate, i18n.resolvedLanguage || 'en');
    const open = openId === rowId;

    const title = (
      <>
        <p className="text-sm font-medium text-text-primary">
          <span>{plainName || condition.name}</span>
          {showClinicalName && <span className="font-normal text-text-secondary"> ({condition.name})</span>}
        </p>
        {firstNoted && <p className="text-sm text-text-secondary mt-0.5">{t('records.firstNoted', { date: firstNoted })}</p>}
      </>
    );

    return (
      <div key={rowId} data-testid={`row-condition-${rowId}`}>
        <div className={`flex items-start justify-between gap-3 ${rowPadding} py-2.5`}>
          {meaning ? (
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => setOpenId(open ? null : rowId)}
              aria-expanded={open}
              data-testid={`button-toggle-condition-${rowId}`}
            >
              {title}
            </button>
          ) : (
            <div className="min-w-0 flex-1">{title}</div>
          )}
          <span className="flex items-center gap-2 flex-shrink-0">
            <ExplainButton
              onClick={() => onAskQuestion(t('explain.condition', { name: condition.name, patientFirstName }))}
              ariaLabel={t('explain.conditionAriaLabel', { name: condition.name })}
              testId={`button-explain-${slugify(condition.name)}`}
            />
            {meaning && (open ? <ChevronDown className="h-4 w-4 text-text-secondary" /> : <ChevronRight className="h-4 w-4 text-text-secondary" />)}
          </span>
        </div>
        {meaning && open && (
          <div className={`${rowPadding} pb-3`} data-testid={`meaning-${rowId}`}>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1">{t('records.termMeans')}</p>
            <p className="text-sm text-text-primary leading-relaxed">{meaning}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <ClinicalDataLanguageNotice />
      {groups.filter((group) => group.conditions.length > 0).map((group) => (
        <div key={group.id} className="mb-4" data-testid={`conditions-group-${group.id}`}>
          <h3 className="text-sm font-semibold text-text-primary mb-2">{group.label}</h3>
          <div className="rounded-lg border bg-white divide-y">
            {group.conditions.map((condition, index) => renderRow(condition, index, group.id))}
          </div>
        </div>
      ))}
    </div>
  );
}
